import { BaseEntity } from "@bt-common/schemas/bt/base-entity";

export const ALLOWED_WEBHOOK_EVENT_NAMES = [
  "new_message_received_webhook",
  "new_message_received_from_me_webhook",
  "message_deleted_revoked_webhook",
  "message_edited_webhook",
  "poll_vote_webhook",
  "message_reaction_webhook",
  "ack_changed_webhook",
  "reply_to_my_message_webhook",
  "participant_joined_via_link_webhook",
  "participant_added_by_admin_webhook",
  "participant_left_group_webhook",
  "participant_kicked_from_group_webhook",
  "group_admin_changed_webhook",
  "group_name_changed_webhook",
  "group_description_changed_webhook",
  "group_message_pinned_webhook",
] as const;

export type WebhookEventName = (typeof ALLOWED_WEBHOOK_EVENT_NAMES)[number];

export const WEBHOOK_EVENT_LABELS: Record<WebhookEventName, string> = {
  new_message_received_webhook: "New Message Received",
  new_message_received_from_me_webhook: "New Message Received (Sent by Me)",
  message_deleted_revoked_webhook: "Message Deleted / Revoked",
  message_edited_webhook: "Message Edited",
  poll_vote_webhook: "Poll Vote",
  message_reaction_webhook: "Message Reaction",
  ack_changed_webhook: "Ack Changed",
  reply_to_my_message_webhook: "Reply To My Message",
  participant_joined_via_link_webhook: "Participant Joined via Link",
  participant_added_by_admin_webhook: "Participant Added by Admin",
  participant_left_group_webhook: "Participant Left Group",
  participant_kicked_from_group_webhook: "Participant Kicked from Group",
  group_admin_changed_webhook: "Group Admin Changed",
  group_name_changed_webhook: "Group Name Changed",
  group_description_changed_webhook: "Group Description Changed",
  group_message_pinned_webhook: "Group Message Pinned",
};

// Single source of truth for how events are grouped/ordered in the dashboard
// panel. The panel renders straight from this — it must NOT keep its own copies
// of the event-name lists (they drift when a new event is added to the model).
export const WEBHOOK_EVENT_GROUPS = [
  {
    key: "message",
    labelKey: "api.webhookMessageEventsGroup",
    events: [
      "new_message_received_webhook",
      "new_message_received_from_me_webhook",
      "message_deleted_revoked_webhook",
      "message_edited_webhook",
      "poll_vote_webhook",
      "message_reaction_webhook",
      "ack_changed_webhook",
      "reply_to_my_message_webhook",
    ],
  },
  {
    key: "chat",
    labelKey: "api.webhookChatEventsGroup",
    events: [
      "participant_joined_via_link_webhook",
      "participant_added_by_admin_webhook",
      "participant_left_group_webhook",
      "participant_kicked_from_group_webhook",
      "group_admin_changed_webhook",
      "group_name_changed_webhook",
      "group_description_changed_webhook",
      "group_message_pinned_webhook",
    ],
  },
] as const satisfies ReadonlyArray<{
  key: string;
  labelKey: string;
  events: readonly WebhookEventName[];
}>;

export const isEventEnabled = (
  w: Pick<Webhook, "events">,
  name: string,
): boolean => (w.events ?? []).includes(name);

export const toggleEvent = (
  events: string[] = [],
  name: string,
  on: boolean,
): string[] =>
  on ? Array.from(new Set([...events, name])) : events.filter((e) => e !== name);

export class Webhook implements BaseEntity {
  // BaseEntity fields (Mongoose doc identifiers + sync-engine bookkeeping).
  _id?: string;
  tempId?: string;
  version?: number;
  dirtySessionId?: string;
  dirty?: boolean;
  // Soft-delete flag — the panel patches `{ deleted: true }` instead of a
  // hard delete, matching the sync-engine's soft-delete convention.
  deleted?: boolean;

  // Webhook-specific
  url?: string;
  owner?: string;
  enabled?: boolean;
  events?: string[];

  constructor(data: Partial<Webhook>) {
    Object.assign(this, data);
  }
}
